import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { formatCurrency, formatDate } from '../../utils/api'
import '../../styles/pages.css'
import './affiliates.css'

// Public, no-login partner portal at /marketing/referrals/:slug. Uses raw fetch
// (no credentials) against the public affiliate endpoints. No app shell / nav.
const API_BASE = import.meta.env.VITE_API_URL || ''

function ReferralPortal() {
  const { slug } = useParams()
  const [portal, setPortal] = useState(null)
  const [status, setStatus] = useState('loading') // loading | ready | notfound | error
  const [adding, setAdding] = useState(false)

  useEffect(() => { load() }, [slug])

  async function load() {
    setStatus('loading')
    try {
      const res = await fetch(`${API_BASE}/api/affiliates/portal/${encodeURIComponent(slug)}`)
      if (res.status === 404) return setStatus('notfound')
      if (!res.ok) return setStatus('error')
      const data = await res.json()
      setPortal(data.portal)
      setStatus('ready')
    } catch (error) {
      console.error('Portal load failed:', error)
      setStatus('error')
    }
  }

  async function addCustomer(body) {
    const res = await fetch(`${API_BASE}/api/affiliates/portal/${encodeURIComponent(slug)}/referred-customers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || 'Could not add customer')
    setAdding(false)
    await load()
  }

  async function removeCustomer(id, name) {
    if (!confirm(`Remove ${name}? They'll no longer be tracked as a referral. You can re-add them later.`)) return
    try {
      const res = await fetch(`${API_BASE}/api/affiliates/portal/${encodeURIComponent(slug)}/referred-customers/${id}`, { method: 'DELETE' })
      if (!res.ok) throw new Error()
      await load()
    } catch {
      alert('Could not remove customer. Please try again.')
    }
  }

  if (status === 'loading') return <Centered><div className="affiliate-loading">Loading…</div></Centered>
  if (status === 'notfound') return <Centered><h2>Portal not found</h2><p className="text-muted">Please check the link you were given.</p></Centered>
  if (status === 'error') return <Centered><h2>Something went wrong</h2><p className="text-muted">Please try again shortly.</p></Centered>

  const { referrer, referred_customers, commissions, payments, total_owed } = portal

  return (
    <Centered wide>
      <div className="pageHead">
        <h1>{referrer.name} — Referrals</h1>
        <p>Add the customers you're referring to Audio Trader. You earn {referrer.bonus_pct}% of each referred trade once it completes.</p>
      </div>

      <div className="card" style={{ padding: 20, marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div className="text-sm text-muted">Currently owed to you</div>
          <div style={{ fontSize: 30, fontWeight: 700 }}>{formatCurrency(total_owed)}</div>
        </div>
        <div className="text-sm text-muted" style={{ maxWidth: 320 }}>
          We pay this out periodically. Completed trades and payments are listed below.
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0' }}>
        <h3 style={{ margin: 0 }}>Customers you're referring</h3>
        {!adding && <button className="btn btn-primary" onClick={() => setAdding(true)}>Add a customer</button>}
      </div>
      {adding && <CustomerForm onSave={addCustomer} onCancel={() => setAdding(false)} />}
      {referred_customers.length === 0 ? (
        <p className="text-muted">No customers added yet. Add the first one above.</p>
      ) : (
        <table className="table">
          <thead><tr><th>Name</th><th>Email domain</th><th>Notes</th><th></th></tr></thead>
          <tbody>
            {referred_customers.map(c => (
              <tr key={c.id}>
                <td>{c.name}</td>
                <td><code>@{c.email_domain}</code></td>
                <td className="text-sm text-muted">{c.notes || '-'}</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="btn btn-sm btn-secondary" onClick={() => removeCustomer(c.id, c.name)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3 style={{ marginTop: 28 }}>Completed referrals</h3>
      {commissions.length === 0 ? (
        <p className="text-muted">Nothing yet — referrals appear here once the customer's trade completes.</p>
      ) : (
        <table className="table">
          <thead><tr><th>Date</th><th>Trade</th><th>You earn</th><th>Status</th></tr></thead>
          <tbody>
            {commissions.map((c, i) => (
              <tr key={i}>
                <td>{formatDate(c.accrued_at)}</td>
                <td>{c.deal_name || c.items_summary || 'Trade'}</td>
                <td><strong>{formatCurrency(c.commission_amount)}</strong></td>
                <td>{c.status === 'paid' ? <span className="badge badge-success">Paid</span> : <span className="badge">Due</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {payments.length > 0 && (
        <>
          <h3 style={{ marginTop: 28 }}>Payments to you</h3>
          <table className="table">
            <thead><tr><th>Date</th><th>Amount</th><th>Reference</th></tr></thead>
            <tbody>
              {payments.map((p, i) => (
                <tr key={i}><td>{formatDate(p.paid_at)}</td><td><strong>{formatCurrency(p.amount)}</strong></td><td className="text-sm text-muted">{p.reference || '-'}</td></tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Centered>
  )
}

function CustomerForm({ onSave, onCancel }) {
  const [name, setName] = useState('')
  const [domain, setDomain] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  async function submit() {
    setError('')
    if (!name.trim()) return setError('Name is required')
    if (!domain.trim()) return setError('Email domain is required')
    setSaving(true)
    try {
      await onSave({ name: name.trim(), email_domain: domain.trim(), notes: notes.trim() || null })
    } catch (e) {
      setError(e?.message || 'Could not add customer')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card" style={{ padding: 16, marginBottom: 16 }}>
      <div className="affiliate-form">
        <div className="filter-group">
          <label className="filter-label">Customer name</label>
          <input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="Geoff Smith" maxLength={200} />
        </div>
        <div className="filter-group">
          <label className="filter-label">Their email domain</label>
          <input className="form-input" value={domain} onChange={e => setDomain(e.target.value)} placeholder="@theircompany.com" maxLength={200} />
        </div>
      </div>
      <div className="filter-group filter-group-wide" style={{ marginTop: 12 }}>
        <label className="filter-label">Notes (optional)</label>
        <textarea className="form-input" value={notes} onChange={e => setNotes(e.target.value)} rows={2} maxLength={2000} />
      </div>
      {error && <div className="text-sm" style={{ color: '#c0392b', marginTop: 8 }}>{error}</div>}
      <div className="filter-actions" style={{ marginTop: 12 }}>
        <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving ? 'Adding…' : 'Add customer'}</button>{' '}
        <button className="btn btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </div>
  )
}

function Centered({ children, wide }) {
  return (
    <div style={{ minHeight: '100vh', background: '#f7f7f8', padding: '40px 16px' }}>
      <div style={{ maxWidth: wide ? 860 : 480, margin: '0 auto', background: '#fff', borderRadius: 12, padding: 32, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
        {children}
      </div>
    </div>
  )
}

export default ReferralPortal
